import React, { useEffect, useState } from 'react';
import moment from 'moment-business-days';
import dataCollectFollowUpJson from 'config/tasks/data-collect-follow-up.json';
import { Typography, Box, Modal, Grid } from '@mui/material';
import HTTP from 'services/http';
import { getModalStyle } from 'services/utils/styles-service';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { AreaShippingClient } from 'clients/area-shipping-client';
import { useSelector } from 'react-redux';
import FollowUpDataCollectComp from './follow-up-data-collect-comp';
import PreviousDataCollectComp from './previous-data-collect-comp';
import { styles } from './follow-up-data-collect-modal-style';

const adjustDateForHolidays = (date, holidaysSet) => {
  let adjustedDate = date.clone();

  while (
    holidaysSet.has(adjustedDate.format('YYYY-MM-DD')) ||
    adjustedDate.day() === 0 ||
    adjustedDate.day() === 6
  ) {
    adjustedDate = adjustedDate.add(1, 'days');
  }

  return adjustedDate;
};

const getNewFollowUpDate = (date, days, holidaysSet = new Set()) => {
  const newFollowUpDate = moment(date);
  newFollowUpDate.add(days, 'days');

  if (!newFollowUpDate.isBusinessDay()) {
    newFollowUpDate.nextBusinessDay();
  }

  if (holidaysSet.size > 0) {
    return adjustDateForHolidays(newFollowUpDate, holidaysSet);
  }

  return newFollowUpDate;
};

const FollowUpDataCollectModal = ({
  therapyId,
  selectedPatientId,
  taskType,
  onClose,
  open,
  onSubmit,
  dataCollectedDate,
  minAssessmentDate,
  therapyDuration,
}) => {
  const NEW_DATA_COLLECT = 0;
  const PREVIOUS_DATA_COLLECT = 1;

  const tasksList = useSelector(state => state.tasks.data);
  const selectedWagTaskKeys = useSelector(state => state.tasks.selectedWagTaskKeys);
  const [protocolDataTypes, setProtocolDataTypes] = useState(null);
  const [nextFollowupInDays, setNextFollowupInDays] = useState(null);
  const [tabValue, setTabValue] = useState(NEW_DATA_COLLECT);
  const [holidaysSet, setHolidaysSet] = useState(new Set());

  const classes = styles();

  useEffect(() => {
    AreaShippingClient.fetchShippingHolidays(365).then(res => {
      const data = res.data || {};
      const merged = [...(data.Customer || [])];
      setHolidaysSet(new Set(merged.map(d => moment.utc(d).format('YYYY-MM-DD'))));
    });
  }, []);

  useEffect(() => {
    if (therapyId && !selectedWagTaskKeys.length) {
      HTTP.post(`/pmp/therapies/${therapyId}/protocols/next`, {
        dataCollectedDate,
        minAssessmentDate,
        therapyDuration,
      }).then(response => {
        setProtocolDataTypes(
          response.data.protocols.reduce((acc, curr) => {
            acc.push({
              clinicalDataTypeId: curr[0],
              required: true,
            });
            setNextFollowupInDays(curr[1]); // # of days till next protocol
            return acc;
          }, []),
        );
      });
    }
    if (therapyId && selectedWagTaskKeys.length) {
      const therapyIdsBeingWag = selectedWagTaskKeys
        .map(wagTask => tasksList[`${wagTask}`])
        .map(tt => tt.therapy_id);
      if (therapyIdsBeingWag && therapyIdsBeingWag.length) {
        // eslint-disable-next-line no-inner-declarations
        async function setOngoingProtocolDataTypeWhenWag() {
          const tempProtocolDataTypes = [];
          for (const wagTherapyId of therapyIdsBeingWag) {
            const res = await HTTP.post(`/pmp/therapies/${wagTherapyId}/protocols/next`, {
              dataCollectedDate,
              minAssessmentDate,
              therapyDuration,
            });
            if (res?.data?.protocols && res.data.protocols.length) {
              for (const curr of res.data.protocols) {
                const protocolTypeExist = tempProtocolDataTypes.find(
                  p => p.clinicalDataTypeId === curr[0],
                );
                if (!protocolTypeExist) {
                  tempProtocolDataTypes.push({
                    clinicalDataTypeId: curr[0],
                    required: true,
                  });
                  setNextFollowupInDays(curr[1]); // # of days till next protocol
                }
              }
            }
          }
          setProtocolDataTypes(tempProtocolDataTypes);
        }
        setOngoingProtocolDataTypeWhenWag();
      }
    }
  }, [therapyId]);

  return protocolDataTypes ? (
    <Modal open={open}>
      <div style={getModalStyle()} className={classes.modal}>
        <Grid
          justifyContent="space-between"
          alignItems="center"
          className={classes.headerContainer}
        >
          <Grid item xs={12} className={classes.header}>
            Create New Data Collect
          </Grid>
        </Grid>
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          aria-label="Data collect tabs"
          className={classes.tabs}
        >
          <Tab label="New Data Collect" />
          <Tab label="Previous Data Collect" />
        </Tabs>
        {tabValue === NEW_DATA_COLLECT && (
          <FollowUpDataCollectComp
            instructions={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <Typography component="span">
                <Box fontWeight={500}>Task settings</Box>
              </Typography>
            }
            jsonForm={dataCollectFollowUpJson}
            formId="new-task-form"
            formName="select"
            data={{
              follow_up_date: getNewFollowUpDate(moment(), nextFollowupInDays, holidaysSet),
              data_collect: { valid: true, values: protocolDataTypes },
            }}
            providers={{
              task: {},
            }}
            onSubmit={updatedTask => {
              onSubmit({
                follow_up_date: updatedTask.follow_up_date,
                follow_up_data_collect: updatedTask.data_collect.values,
              });
              return true;
            }}
            submitButtonText="Create Task"
            cancelButtonText="Cancel"
            alwaysEnabled
            onCancel={() => {
              if (onClose) {
                onClose();
              }
            }}
            marginTop
          />
        )}
        {tabValue === PREVIOUS_DATA_COLLECT && (
          <PreviousDataCollectComp selectedPatientId={selectedPatientId} taskType={taskType} />
        )}
      </div>
    </Modal>
  ) : (
    therapyId && <div>Loading...</div>
  );
};

export default FollowUpDataCollectModal;
